import React from 'react';
import styled, { css } from 'styled-components';

import useStores from 'shared/hooks/use-stores';
import { AccordionBanner } from 'src/payments/components/accordion-banner/accordion-banner';
import BankLogosSmall from 'src/assets/bank-logos-small';
import { mediaQueries } from 'shared/styles';
import { CheckoutLabelGrouping } from 'checkout/components/styles/common';
import { DUTCHIE_PAY, PBB_INCENTIVES_TERMS_URL } from 'src/payments/constants';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { useIncentives, UserType } from 'src/payments/hooks/use-incentives/use-incentives';
import MultiDiscount from './assets/multi-discount';

const cashBackText = (isEnrolledDutchiePay: boolean, value: number, paymentMethod: string): JSX.Element =>
  isEnrolledDutchiePay && paymentMethod === DUTCHIE_PAY ? (
    <span>
      You're getting <strong>${value} cash back</strong> by checking out with <strong>Pay by Bank</strong>
    </span>
  ) : (
    <span>
      Get <strong>${value} cash back</strong> when you checkout with <strong>Pay by Bank</strong>
    </span>
  );

const discountText = (isEnrolledDutchiePay: boolean, value: number, paymentMethod: string): JSX.Element =>
  isEnrolledDutchiePay && paymentMethod === DUTCHIE_PAY ? (
    <span>
      You're getting <strong>${value} off</strong> your order with <br /> <strong>Pay by Bank</strong>
    </span>
  ) : (
    <span>
      Get <strong>${value} off</strong> your first order with <br /> <strong>Pay by Bank</strong>.
    </span>
  );

export const PBBIncentiveBanner = ({ marginBottom }): JSX.Element | null => {
  const { isEnrolledDutchiePay } = useDutchiePay();
  const { incentiveValue, isCashBack } = useIncentives();
  const { Cart } = useStores();

  if (!incentiveValue) {
    return null;
  }

  return (
    <AccordionBanner
      marginBottom={marginBottom}
      borderRadius='4px 4px 4px 4px'
      graphic={<MultiDiscount amount={incentiveValue} />}
      mainContent={
        isCashBack
          ? cashBackText(isEnrolledDutchiePay, incentiveValue, Cart.paymentMethod)
          : discountText(isEnrolledDutchiePay, incentiveValue, Cart.paymentMethod)
      }
      toggleContent={
        <span>
          {Cart.paymentMethod !== DUTCHIE_PAY && 'Select "Pay now" as your Payment option, link your bank & checkout. '}
          {isCashBack
            ? `Your bank statement will show two transactions, a charge for your order total and a deposit of $${incentiveValue}.`
            : 'Only valid the first time you use Pay by Bank to pay for your order. '}
          <a href={PBB_INCENTIVES_TERMS_URL} target='_blank' rel='noreferrer'>
            Terms apply
          </a>
        </span>
      }
    />
  );
};

export const PBBIncentiveBannerWithCTA = (): JSX.Element => {
  const { Cart, UI } = useStores();
  const { incentiveValue, userType, isCashBack } = useIncentives();
  const { bankAccounts } = useDutchiePay();
  const paymentMethodId = bankAccounts.length > 0 ? bankAccounts[0].id : undefined;

  const { openDutchiePay } = useOpenDutchiePay({
    cname: Cart.dispensary.cName ?? '',
    enrollmentSource: `checkout-page-${UI?.variant}`,
  });

  function setPaymentToPBB(): void {
    Cart.setPaymentMethodId(paymentMethodId);
    Cart.setPaymentMethod(DUTCHIE_PAY);
    if (UI.variant === 'embedded') {
      UI.embeddedEventDispatcher(`iframe:scrollToTop`, { force: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  return (
    <CheckoutLabelGrouping mb='20px'>
      <Container>
        <BankLogosSmall width='50px' />
        <span>
          {isCashBack ? (
            <>
              Get <strong>${incentiveValue} cash back</strong> when you use <strong>Pay by Bank</strong> to checkout.
            </>
          ) : (
            <>
              Get <strong>${incentiveValue} off</strong> your order the first time you use <strong>Pay by Bank</strong>{' '}
              to checkout.
            </>
          )}
          <TextButton onClick={userType === UserType.UNENROLLED ? openDutchiePay : setPaymentToPBB}>
            Link your bank
          </TextButton>
        </span>
      </Container>
    </CheckoutLabelGrouping>
  );
};

const Container = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    align-items: center;
    background-color: ${colors.green20};
    border-radius: 4px;
    display: flex;
    height: 65px;
    justify-content: flex-start;
    width: 390px;
    color: ${colors.green90};
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    width: 340px;
    margin-top: 20px;
    gap: ${spaces[2]};

    span {
      max-width: 250px;
    }

    a {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  @media ${mediaQueries.largePhone} {
    width: 100%;

    span {
      max-width: 300px;
    }
  }
`}
`;

const TextButton = styled.button`
  display: block;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  font-weight: 600;
`;
